import { useMemo } from 'react'

import { useWindow, useQueryResult } from '@krp/client-runtime'
import {
  StructureScreen,
  makeSelectStructureScreenVm,
  normalizeRid,
  onecStructure,
  onecStructureRefresh,
  reloadStructure,
  structureNodeSelected,
  structureNodeToggled,
  structureSearchChanged,
  type StructureScreenActions,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'

/**
 * Binding окна «Структура 1С» (ONEC-STRUCT): rid — из окна (D-064). Структура —
 * тот же запрос, из которого берут склады поставки, инвентаризация и ценники;
 * «Обновить из 1С» перечитывает её на сервере и кладёт ответ в общий кэш.
 */
export function StructureBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = normalizeRid(window.params.rid ?? '')
  const args = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const structure = useQueryResult(onecStructure, args)
  const intent = useAppSelector((state) => state.onec.structure)
  const [refresh, refreshState] = onecStructureRefresh.useMutation({ fixedCacheKey: `onec-structure-refresh:${resourceId}` })

  const selectVm = useMemo(() => makeSelectStructureScreenVm(), [])
  const vm = selectVm({
    resourceId,
    structure,
    intent,
    // Время нажатия: отказ «Обновить» не перекрывает чтение, ответившее позже (окно открыли снова).
    refresh: { isLoading: refreshState.isLoading, error: refreshState.error, startedTimeStamp: refreshState.startedTimeStamp },
  })

  const actions = useMemo<StructureScreenActions>(
    () => ({
      searchChanged: (search) => dispatch(structureSearchChanged({ resourceId, search })),
      toggled: (id) => dispatch(structureNodeToggled({ resourceId, id })),
      selected: (id) => dispatch(structureNodeSelected({ resourceId, id })),
      refresh: () => {
        if (resourceId !== '') void refresh({ resourceId })
      },
      retry: () => dispatch(reloadStructure(resourceId)),
    }),
    [dispatch, refresh, resourceId],
  )

  return <StructureScreen vm={vm} actions={actions} />
}
